import { useState } from 'react'
import { VolunteerTable } from '@/features/volunteer'
import { ParentLogin, useAuth } from '@/features/auth'

function UserMenu() {
  const { user, signOut, isAdmin } = useAuth()
  const [showMenu, setShowMenu] = useState(false)

  const handleSignOut = async () => {
    await signOut()
    setShowMenu(false)
  }

  const navigate = (path: string) => {
    window.location.href = path
    setShowMenu(false)
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="flex items-center text-sm bg-white/90 backdrop-blur-sm border border-gray-200 rounded-2xl px-4 py-3 hover:bg-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 shadow-sm"
      >
        <div className="w-8 h-8 bg-gradient-to-r from-primary to-accent rounded-full flex items-center justify-center ml-3">
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <span className="font-hebrew text-gray-700 font-medium">
          {user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'משתמש'}
        </span>
        <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {showMenu && (
        <div className="absolute left-0 mt-2 w-56 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-xl z-10 overflow-hidden">
          <div className="py-2">
            <div className="px-4 py-3 text-sm text-gray-500 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-blue-50/30">
              <div className="font-hebrew font-medium text-gray-700">חשבון משתמש</div>
              <div className="text-xs text-gray-500 mt-1">{user?.email}</div>
            </div>
            {isAdmin && (
              <button
                onClick={() => navigate('/admin/reminders')}
                className="w-full text-right px-4 py-3 text-sm text-blue-600 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 font-hebrew font-medium transition-all duration-200 flex items-center gap-2 border-b border-gray-100"
              >
                <svg className="w-4 h-4 mr-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                ניהול תזכורות
              </button>
            )}
            <button
              onClick={handleSignOut}
              className="w-full text-right px-4 py-3 text-sm text-red-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 font-hebrew font-medium transition-all duration-200 flex items-center gap-2"
            >
              <svg className="w-4 h-4 mr-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              יציאה מהמערכת
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export function HomePage() {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-hebrew">טוען...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <ParentLogin />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50 dir-rtl" dir="rtl">
      <div className="container mx-auto px-4 py-8">
        <header className="text-center mb-12">
          <div className="flex justify-between items-center mb-6">
            <div className="w-16"></div>
            <div className="text-center">
              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 font-hebrew mb-2">
                מערכת תורנות הורים - אילנות
              </h1>
              <div className="w-24 h-1 bg-gradient-to-r from-primary to-accent rounded-full mx-auto"></div>
            </div>
            <UserMenu />
          </div>
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-gray-200 max-w-2xl mx-auto">
            <p className="text-xl text-gray-700 font-hebrew font-semibold mb-2">
              רישום פשוט לתורנות "נשק וסע"
            </p>
            <div className="flex items-center justify-center gap-4 text-gray-600">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-hebrew font-medium">שעות תורנות: 07:30-08:10</span>
              </div>
            </div>
          </div>
        </header>
        <main className="max-w-6xl mx-auto">
          <VolunteerTable />
        </main>
      </div>
    </div>
  )
}
